import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import transactionService from '../../services/transactionService'
import { addAuditLog } from './auditSlice'

const normalizeRule = (r) => {
  if (!r) return ''
  if (typeof r === 'string') return r
  return r.name || r.rule_name || r.ruleId || r.rule_id || JSON.stringify(r)
}

const normalizeTxn = (t = {}) => ({
  id: t.id,
  timestamp: t.timestamp,
  amount: t.amount || 0,
  currency: t.currency || 'INR',

  merchantId: t.merchantId ?? t.merchant_id ?? '',
  merchantName: t.merchantName ?? t.merchant_name ?? '',
  merchantCategory: t.merchantCategory ?? t.merchant_category ?? '',

  customerId: t.customerId ?? t.customer_id ?? '',
  customerName: t.customerName ?? t.customer_name ?? '',

  paymentMethod: t.paymentMethod ?? t.payment_method ?? '',
  status: t.status || 'APPROVED',

  riskScore: t.riskScore ?? t.risk_score ?? 0,
  riskLevel: t.riskLevel ?? t.risk_level ?? 'LOW',

  triggeredRules: (t.triggeredRules ?? t.triggered_rules ?? []).map(normalizeRule),
  triggeredRuleObjects: t.triggeredRules ?? t.triggered_rules ?? [],

  finalDecision: t.finalDecision ?? t.final_decision ?? 'ALLOW',

  city: t.city || '',
  ipAddress: t.ipAddress ?? t.ip_address ?? '',
  deviceId: t.deviceId ?? t.device_id ?? '',
  processingTimeMs: t.processingTimeMs ?? t.processing_time_ms ?? 0,

  rawPayload: t.rawPayload ?? t.raw_payload ?? {},
  payload: t.payload ?? t.raw_payload ?? t.rawPayload ?? {},

  createdAt: t.createdAt ?? t.created_at,

  timeline: t.timeline ?? [
    { step: 'Request received', ms: 5, status: 'passed' },
    { step: 'Rule evaluation', ms: t.processing_time_ms ?? t.processingTimeMs ?? 20, status: (t.triggered_rules || t.triggeredRules || []).length ? 'triggered' : 'passed' },
    { step: 'Decision generated', ms: 3, status: 'passed' },
  ],

  riskBreakdown: t.riskBreakdown ?? [
    { factor: 'Rule score', score: t.risk_score ?? t.riskScore ?? 0, max: 100 },
    { factor: 'Velocity risk', score: Math.min((t.risk_score ?? t.riskScore ?? 0) * 0.4, 40), max: 40 },
    { factor: 'Merchant risk', score: Math.min((t.risk_score ?? t.riskScore ?? 0) * 0.3, 30), max: 30 },
  ],

  merchantContext: t.merchantContext ?? {
    name: t.merchant_name ?? t.merchantName ?? '',
    category: t.merchant_category ?? t.merchantCategory ?? '',
    riskTier: (t.risk_level ?? t.riskLevel ?? 'LOW'),
    chargebackRate: 0.45,
    totalTxns30d: 1250,
    joinedDate: t.created_at ?? t.createdAt ?? new Date().toISOString(),
  },

  customerContext: t.customerContext ?? {
    id: t.customer_id ?? t.customerId ?? '',
    name: t.customer_name ?? t.customerName ?? '',
    accountAgeDays: 180,
    totalTxns: 42,
    flaggedCount: (t.triggered_rules || t.triggeredRules || []).length,
  },

  comments: t.comments ?? [],
})

export const fetchTransactions = createAsyncThunk(
  'transactions/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const res = await transactionService.getList(params)
      return {
        ...res,
        data: (res.data || []).map(normalizeTxn),
      }
    } catch (e) {
      return rejectWithValue(e.message)
    }
  }
)

export const fetchTransactionById = createAsyncThunk(
  'transactions/fetchById',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await transactionService.getById(id)
      dispatch(addAuditLog({ action: 'TXN_VIEWED', subject: 'Transaction', meta: { txnId: id } }))
      return normalizeTxn(data)
    } catch (e) {
      return rejectWithValue(e.message)
    }
  }
)

export const fetchRelatedTransactions = createAsyncThunk(
  'transactions/fetchRelated',
  async (id, { rejectWithValue }) => {
    try {
      const res = await transactionService.getRelated(id)
      return {
        ...res,
        data: (res.data || []).map(normalizeTxn),
      }
    } catch (e) {
      return rejectWithValue(e.message)
    }
  }
)

export const submitManualDecision = createAsyncThunk(
  'transactions/manualDecision',
  async ({ id, decision }, { dispatch, rejectWithValue }) => {
    try {
      const data = await transactionService.manualDecision(id, decision)
      dispatch(addAuditLog({ action: 'MANUAL_DECISION', subject: 'Transaction', meta: { txnId: id, decision } }))
      return data
    } catch (e) {
      return rejectWithValue(e.message)
    }
  }
)

export const addAnalystComment = createAsyncThunk(
  'transactions/addComment',
  async ({ id, comment }, { dispatch, rejectWithValue }) => {
    try {
      const data = await transactionService.addComment(id, comment)
      dispatch(addAuditLog({ action: 'COMMENT_ADDED', subject: 'Transaction', meta: { txnId: id } }))
      return data
    } catch (e) {
      return rejectWithValue(e.message)
    }
  }
)

const transactionSlice = createSlice({
  name: 'transactions',
  initialState: {
    list: [],
    total: 0,
    page: 1,
    limit: 50,
    selected: null,
    related: [],
    loading: { list: false, detail: false, related: false, action: false },
    errors: {},
    filters: {
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: '',
      merchant: '',
      customer: '',
      riskLevel: '',
      status: '',
      paymentMethod: '',
      ruleHit: '',
      search: '',
    },
    drawerOpen: false,
  },
  reducers: {
    setTxnFilter(state, { payload }) {
      state.filters = { ...state.filters, ...payload }
      state.page = 1
    },
    resetTxnFilters(state) {
      state.filters = {
        dateFrom: '',
        dateTo: '',
        amountMin: '',
        amountMax: '',
        merchant: '',
        customer: '',
        riskLevel: '',
        status: '',
        paymentMethod: '',
        ruleHit: '',
        search: '',
      }
      state.page = 1
    },
    setPage(state, { payload }) {
      state.page = payload
    },
    openDrawer(state, { payload }) {
      state.selected = normalizeTxn(payload)
      state.drawerOpen = true
    },
    closeDrawer(state) {
      state.drawerOpen = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (s) => {
        s.loading.list = true
        s.errors.list = null
      })
      .addCase(fetchTransactions.rejected, (s, a) => {
        s.loading.list = false
        s.errors.list = a.payload
      })
      .addCase(fetchTransactions.fulfilled, (s, a) => {
        s.loading.list = false
        s.list = a.payload.data || []
        s.total = a.payload.total || 0
        s.page = a.payload.page || s.page
        s.limit = a.payload.limit || s.limit
      })

      .addCase(fetchTransactionById.pending, (s) => {
        s.loading.detail = true
        s.errors.detail = null
      })
      .addCase(fetchTransactionById.rejected, (s, a) => {
        s.loading.detail = false
        s.errors.detail = a.payload
      })
      .addCase(fetchTransactionById.fulfilled, (s, a) => {
        s.loading.detail = false
        s.selected = a.payload
        s.drawerOpen = true
      })

      .addCase(fetchRelatedTransactions.pending, (s) => {
        s.loading.related = true
      })
      .addCase(fetchRelatedTransactions.fulfilled, (s, a) => {
        s.loading.related = false
        s.related = a.payload.data || []
      })
      .addCase(fetchRelatedTransactions.rejected, (s) => {
        s.loading.related = false
      })

      .addCase(submitManualDecision.pending, (s) => {
        s.loading.action = true
      })
      .addCase(submitManualDecision.fulfilled, (s, a) => {
        s.loading.action = false
        const patch = {
          status: a.payload.status,
          finalDecision: a.payload.decision === 'APPROVE' ? 'ALLOW' : 'BLOCK',
        }
        if (s.selected?.id === a.payload.id) s.selected = { ...s.selected, ...patch }
        s.list = s.list.map(t => t.id === a.payload.id ? { ...t, ...patch } : t)
      })
      .addCase(submitManualDecision.rejected, (s) => {
        s.loading.action = false
      })

      .addCase(addAnalystComment.fulfilled, (s, a) => {
        if (s.selected) {
          const comment = a.payload.comment
          s.selected = {
            ...s.selected,
            comments: [...(s.selected.comments || []), comment],
          }
        }
      })
  },
})

export const { setTxnFilter, resetTxnFilters, setPage, openDrawer, closeDrawer } = transactionSlice.actions

export const selectTxnList = s => s.transactions.list
export const selectTxnTotal = s => s.transactions.total
export const selectTxnSelected = s => s.transactions.selected
export const selectTxnRelated = s => s.transactions.related
export const selectTxnFilters = s => s.transactions.filters
export const selectTxnPage = s => s.transactions.page
export const selectTxnLimit = s => s.transactions.limit
export const selectTxnLoading = s => s.transactions.loading
export const selectDrawerOpen = s => s.transactions.drawerOpen

export default transactionSlice.reducer